
import React from 'react';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';

interface LineChartProps {
  title: string;
  data: Array<Record<string, unknown>>;
  lines: {
    name: string;
    dataKey: string;
    color: string;
  }[];
  yAxisLabel?: string;
  xAxisLabel?: string;
  description?: string;
}

export function LineChart({
  title,
  data,
  lines,
  yAxisLabel,
  xAxisLabel,
  description,
}: LineChartProps) {
  const categories = (data || []).map((d) => d?.name);

  const series = lines.map((line) => ({
    name: line.name,
    data: (data || []).map((d) => {
      const v = d?.[line.dataKey];
      return typeof v === 'number' ? v : Number(v) || 0;
    }),
  }));

  const options: ApexOptions = {
    chart: {
      type: 'line',
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: {
        enabled: true,
        speed: 1500,
      },
      fontFamily: 'inherit',
    },
    colors: lines.map((l) => l.color),
    stroke: {
      curve: 'smooth',
      width: 2,
    },
    dataLabels: { enabled: false },
    markers: {
      size: 0,
      hover: { size: 8 },
    },
    grid: {
      strokeDashArray: 3,
      borderColor: 'rgba(148, 163, 184, 0.3)',
      padding: { top: 20, right: 30, left: 20, bottom: 20 },
    },
    xaxis: {
      categories,
      labels: { style: { fontSize: '12px' } },
      // ApexCharts crashes on undefined title — always pass an object.
      title: { text: xAxisLabel ?? '', offsetY: 10 },
    },
    yaxis: {
      labels: { style: { fontSize: '12px' } },
      title: { text: yAxisLabel ?? '', style: { fontSize: '12px' } },
    },
    legend: {
      show: true,
      position: 'bottom',
      horizontalAlign: 'center',
    },
    tooltip: {
      shared: true,
      intersect: false,
    },
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <div className="h-80 w-full">
          <Chart
            options={options}
            series={series}
            type="line"
            height="100%"
            width="100%"
          />
        </div>
      </CardContent>
    </Card>
  );
}
